import React, { useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Gift } from 'lucide-react';

const SEGMENTS = [
  { label: '5% Off', color: '#C7871F' },
  { label: '10% Off', color: '#007AFF' },
  { label: 'Free Drink', color: '#10B981' },
  { label: '$5 Credit', color: '#8B5CF6' },
  { label: '$10 Credit', color: '#F59E0B' },
  { label: '15% Off', color: '#EF4444' },
  { label: 'Free Dessert', color: '#EC4899' },
  { label: 'Try Again', color: '#52525B' },
];

export default function SpinWheel({ onClose }) {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);

  const spin = async () => {
    setSpinning(true);
    setResult(null);
    try {
      const { data } = await api.post('/rewards/spin');
      const rewardIdx = SEGMENTS.findIndex(s => s.label === data.reward_name);
      const segAngle = 360 / SEGMENTS.length;
      const targetAngle = 360 * 5 + (360 - (rewardIdx * segAngle + segAngle / 2));
      setRotation(prev => prev + targetAngle);
      setTimeout(() => {
        setResult(data);
        setSpinning(false);
      }, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Spin failed');
      setSpinning(false);
    }
  };

  const segAngle = 360 / SEGMENTS.length;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="spin-wheel-overlay">
      <div className="bg-bg-secondary border border-white/10 rounded-sm p-6 max-w-sm w-full text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Gift className="w-5 h-5 text-gold" />
          <h2 className="text-2xl font-serif text-white">Lucky Spin!</h2>
        </div>
        <p className="text-zinc-400 font-sans text-sm mb-6">You've spent $200+! Spin to win a reward.</p>

        {/* Wheel */}
        <div className="relative w-64 h-64 mx-auto mb-6">
          {/* Pointer */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10 w-0 h-0 border-l-[10px] border-r-[10px] border-t-[16px] border-l-transparent border-r-transparent border-t-gold" />

          <svg
            viewBox="0 0 200 200"
            className="w-full h-full"
            style={{ transform: `rotate(${rotation}deg)`, transition: spinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none' }}
          >
            {SEGMENTS.map((seg, i) => {
              const startAngle = i * segAngle;
              const endAngle = startAngle + segAngle;
              const startRad = (startAngle - 90) * (Math.PI / 180);
              const endRad = (endAngle - 90) * (Math.PI / 180);
              const x1 = 100 + 95 * Math.cos(startRad);
              const y1 = 100 + 95 * Math.sin(startRad);
              const x2 = 100 + 95 * Math.cos(endRad);
              const y2 = 100 + 95 * Math.sin(endRad);
              const largeArc = segAngle > 180 ? 1 : 0;
              const midAngle = ((startAngle + endAngle) / 2 - 90) * (Math.PI / 180);
              const textX = 100 + 60 * Math.cos(midAngle);
              const textY = 100 + 60 * Math.sin(midAngle);
              const textRotation = (startAngle + endAngle) / 2;
              return (
                <g key={seg.label}>
                  <path d={`M100,100 L${x1},${y1} A95,95 0 ${largeArc},1 ${x2},${y2} Z`} fill={seg.color} stroke="#050505" strokeWidth="1" />
                  <text x={textX} y={textY} fill="white" fontSize="7" fontFamily="Outfit" fontWeight="600" textAnchor="middle" dominantBaseline="middle" transform={`rotate(${textRotation}, ${textX}, ${textY})`}>
                    {seg.label}
                  </text>
                </g>
              );
            })}
            <circle cx="100" cy="100" r="15" fill="#050505" stroke="#C7871F" strokeWidth="2" />
          </svg>
        </div>

        {result ? (
          <div className="animate-fadeIn">
            <p className="text-gold font-serif text-xl mb-2" data-testid="spin-result">
              {result.reward_type === 'none' ? 'Better luck next time!' : `You won: ${result.reward_name}!`}
            </p>
            {result.reward_type !== 'none' && <p className="text-zinc-400 font-sans text-xs mb-4">Reward added to your wallet</p>}
            <Button onClick={onClose} className="bg-gold text-black font-bold rounded-sm font-sans" data-testid="spin-close-button">Continue</Button>
          </div>
        ) : (
          <Button onClick={spin} disabled={spinning} className="bg-gold text-black font-bold rounded-sm hover:bg-gold-light font-sans px-8" data-testid="spin-button">
            {spinning ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : 'SPIN!'}
          </Button>
        )}

        {!result && !spinning && (
          <button onClick={onClose} className="mt-3 text-zinc-500 text-xs font-sans hover:text-zinc-400" data-testid="spin-skip-button">Skip for now</button>
        )}
      </div>
    </div>
  );
}
